import { useState } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { Layers, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useDeleteLoanPolicy, useLoanPolicies } from "@/api/hooks";
import type { LoanPolicy, Location, PatronCategory } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { policyDifferences, scopeLabel } from "../../loanRules";
import { PolicyDialog } from "./PolicyDialog";

type DialogState = { mode: "closed" } | { mode: "edit"; policy: LoanPolicy | null };

export function PolicyExceptions({
  category,
  locations,
}: {
  category: PatronCategory;
  locations: Location[];
}) {
  const { data: policies, isLoading, error } = useLoanPolicies(category.id);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [toDelete, setToDelete] = useState<LoanPolicy | null>(null);

  return (
    <section
      aria-labelledby="policy-exceptions-title"
      className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2
            id="policy-exceptions-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            Exceptions
            {policies && policies.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                {policies.length}
              </span>
            )}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Remplacent les règles par défaut pour un type de document, un site, ou les deux.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setDialog({ mode: "edit", policy: null });
          }}
        >
          <Plus aria-hidden />
          Nouvelle exception
        </Button>
      </header>

      {isLoading ? (
        <div aria-busy="true" aria-label="Chargement des exceptions" className="space-y-3">
          {[0, 1].map((row) => (
            <div
              key={row}
              className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50"
            />
          ))}
        </div>
      ) : error ? (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          Impossible de charger les exceptions : {error.message}
        </p>
      ) : !policies || policies.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center dark:border-gray-600">
          <Layers className="mb-3 h-6 w-6 text-gray-400" aria-hidden />
          <p className="text-sm font-medium text-gray-900 dark:text-white">Aucune exception</p>
          <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
            Les règles par défaut s&apos;appliquent à tous les documents, dans tous les sites. Créez
            une exception pour les DVD, les nouveautés d&apos;un site…
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {policies.map((policy) => (
            <PolicyRow
              key={policy.id}
              policy={policy}
              category={category}
              onEdit={() => {
                setDialog({ mode: "edit", policy });
              }}
              onDelete={() => {
                setToDelete(policy);
              }}
            />
          ))}
        </ul>
      )}

      {dialog.mode === "edit" && (
        <PolicyDialog
          category={category}
          locations={locations}
          policy={dialog.policy}
          onClose={() => {
            setDialog({ mode: "closed" });
          }}
        />
      )}

      {toDelete && (
        <DeletePolicyDialog
          policy={toDelete}
          onClose={() => {
            setToDelete(null);
          }}
        />
      )}
    </section>
  );
}

function PolicyRow({
  policy,
  category,
  onEdit,
  onDelete,
}: {
  policy: LoanPolicy;
  category: PatronCategory;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { what, where } = scopeLabel(policy);
  const differences = policyDifferences(policy, category);

  return (
    <li
      className={cn(
        "group flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border px-4 py-3 transition-colors",
        policy.active
          ? "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
          : "border-dashed border-gray-300 opacity-70 dark:border-gray-600"
      )}
    >
      <div className="min-w-0 flex-1 basis-56">
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {what}
          <span className="font-normal text-gray-500 dark:text-gray-400"> · {where}</span>
          {!policy.active && (
            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              Désactivée
            </span>
          )}
        </p>
        {policy.notes && (
          <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{policy.notes}</p>
        )}
      </div>

      {/* Differences and actions stay together so the actions never wrap under the chips */}
      <div className="flex min-w-0 items-center gap-3 sm:ml-auto">
        <ul
          className="flex flex-wrap justify-end gap-1.5"
          aria-label="Différences avec les règles par défaut"
        >
          {differences.length === 0 ? (
            <li className="text-xs text-gray-500 dark:text-gray-400">
              Identique aux règles par défaut
            </li>
          ) : (
            differences.map((difference) => (
              <li
                key={difference.label}
                className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-gray-700/60 dark:text-gray-200"
              >
                <span className="text-gray-500 dark:text-gray-400">{difference.label} </span>
                <span className="font-medium">{difference.value}</span>
                {difference.defaultValue && (
                  <span className="text-gray-400 line-through decoration-gray-300 dark:text-gray-500">
                    {" "}
                    {difference.defaultValue}
                  </span>
                )}
              </li>
            ))
          )}
        </ul>

        <div className="flex shrink-0 gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onEdit}
            aria-label={`Modifier l'exception ${what} · ${where}`}
          >
            <Pencil aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            aria-label={`Supprimer l'exception ${what} · ${where}`}
            className="text-red-600 hover:text-red-700 dark:text-red-400"
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      </div>
    </li>
  );
}

function DeletePolicyDialog({ policy, onClose }: { policy: LoanPolicy; onClose: () => void }) {
  const deletePolicy = useDeleteLoanPolicy();
  const [error, setError] = useState<string | null>(null);
  const { what, where } = scopeLabel(policy);

  return (
    <AlertDialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-gray-950/40 backdrop-blur-sm" />
        <AlertDialog.Content className="animate-fade-in fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900">
          <AlertDialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
            Supprimer l&apos;exception « {what} · {where} » ?
          </AlertDialog.Title>
          <AlertDialog.Description className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Les prêts en cours gardent leur date de retour. Les prochains prêts suivront la règle
            suivante dans l&apos;ordre de priorité. Pour la suspendre sans la perdre, désactivez-la
            plutôt.
          </AlertDialog.Description>
          {error && (
            <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <AlertDialog.Cancel asChild>
              <Button variant="ghost">Annuler</Button>
            </AlertDialog.Cancel>
            <Button
              variant="destructive"
              disabled={deletePolicy.isPending}
              onClick={() => {
                deletePolicy.mutate(policy.id, {
                  onSuccess: onClose,
                  onError: (err) => {
                    setError(err.message);
                  },
                });
              }}
            >
              {deletePolicy.isPending && <Loader2 className="animate-spin" aria-hidden />}
              Supprimer
            </Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
